import { action, computed, makeAutoObservable } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Amount } from 'src_common/common/amount/Amount';
import {
    validatePositive,
    validateAmountRequire,
    validateAmountOrNothing,
    validatePositiveOrNothing,
} from 'src/domains/players/shared/validatorsNew/validateRequire';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { FilterOptionType, FilterValueType } from 'src/components/RealityCheckForm/CustomDropdown';
import { convertToNumber, validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { putRollingLimits } from 'src_server/types/responsibleGambling';
import {
    CALCULATION_TYPE_ARRAY,
    DepositLimitCalculationType,
    FULL_OPTION_LENGTH_DAYS_OPTIONS_ARRAY,
    LIMIT_TYPE_ARRAY,
} from 'src/components/DepositLimitTab/NetDepositLimit/NetDepositLimit.state';

export const ROLLING_SEND_EMAIL_WARNING_PERIODS = new Set([30, 90, 365]);

export const GROSS_CALCULATION_ONLY_FOR_CALENDAR_MESSAGE =
    'Gross deposit limits must be offered only in a calendar fixed type.';

export class NetDepositLimitConfigurationState {
    public readonly selectedPeriod: FormInputState<string, number>;
    public readonly limitType: FormInputState<string, string>;
    public readonly calculationType: FormInputState<string, string>;
    public readonly limitValue: FormInputState<string, Amount>;
    public readonly limitValueMax: FormInputState<string, Amount | string>;
    public readonly triggersWarningEmail: FormInputState<boolean, boolean>;
    public readonly isHardLimit: FormInputState<boolean, boolean>;
    public readonly form: FormModel<Omit<putRollingLimits.LimitObjectType, 'limitType' | 'calculationType'>>;

    public constructor(private readonly configState: ConfigState) {
        this.selectedPeriod = FormInputState.new('')
            .map(validateNotEmpty('Please select restriction period.'))
            .map(convertToNumber('This field is required.'));
        this.limitType = FormInputState.new('').map(validateNotEmpty('Please select limit type.'));
        this.calculationType = FormInputState.new('net');

        this.limitValueMax = FormInputState.new('').map(validateAmountOrNothing).map(validatePositiveOrNothing);
        this.limitValue = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive)
            .map((amount): Result<Amount> => {
                const maxRaw = this.limitValueMax.value.trim();

                if (maxRaw === '') {
                    return Result.createOk(amount);
                }

                try {
                    const maxAmount = new Amount(maxRaw);

                    if (amount.isGreaterThan(maxAmount)) {
                        return Result.createError('New limit cannot be higher than Max limit');
                    }
                } catch {
                    return Result.createOk(amount);
                }

                return Result.createOk(amount);
            });
        this.triggersWarningEmail = FormInputState.new(false);
        this.isHardLimit = FormInputState.new(false);

        this.form = FormModel.group({
            windowLengthInDays: this.selectedPeriod,
            limitType: this.limitType,
            pendingLimitValue: this.limitValue.map((amount) => Result.createOk(parseInt(amount.multiply(100).value))),
            limitValueMax: this.limitValueMax.map((amount) =>
                Result.createOk(typeof amount === 'string' ? null : parseInt(amount.multiply(100).value))
            ),
            triggersWarningEmail: this.triggersWarningEmail,
            hardLimit: this.isHardLimit,
        });

        makeAutoObservable(this);
    }

    @computed public get createWindowLengthDaysOptionsArray(): Array<FilterOptionType<string>> {
        return FULL_OPTION_LENGTH_DAYS_OPTIONS_ARRAY;
    }

    @computed public get isSendEmailWarningVisible(): boolean {
        if (this.limitType.value !== 'rolling') {
            return false;
        }

        const periodDays = parseInt(this.selectedPeriod.value, 10);

        return ROLLING_SEND_EMAIL_WARNING_PERIODS.has(periodDays);
    }

    @computed public get effectiveTriggersWarningEmail(): boolean {
        if (!this.isSendEmailWarningVisible) {
            return false;
        }

        return this.triggersWarningEmail.value;
    }

    @computed public get isGrossCalculationAllowed(): boolean {
        return this.limitType.value === 'calendar';
    }

    @computed public get isCalculationTypeLockedToNet(): boolean {
        return (
            this.configState.config.depositLimitCalculationTypeConfigurable === true &&
            this.isGrossCalculationAllowed === false
        );
    }

    @action private syncSendEmailWarningVisibility = (): void => {
        if (!this.isSendEmailWarningVisible) {
            this.triggersWarningEmail.setValue(false);
        }
    };

    @action private syncCalculationTypeWithLimitType = (): void => {
        if (!this.isGrossCalculationAllowed && this.calculationType.value !== 'net') {
            this.calculationType.setValue('net');
        }
    };

    public onUpdatePeriod = (changedValue: FilterValueType): void => {
        if (typeof changedValue === 'string') {
            if (changedValue === '0') {
                this.onUpdateType('lifetime');
            } else if (this.limitType.value === 'lifetime') {
                this.onUpdateType('');
            }
            this.selectedPeriod.setValue(changedValue);
            this.syncSendEmailWarningVisibility();
        }
    };

    public onUpdateType = (changedValue: FilterValueType): void => {
        if (typeof changedValue === 'string') {
            this.limitType.setValue(changedValue);
            this.syncSendEmailWarningVisibility();
            this.syncCalculationTypeWithLimitType();
        }
    };

    public onUpdateCalculationType = (changedValue: FilterValueType): void => {
        if (changedValue === 'gross' && this.isGrossCalculationAllowed === false) {
            return;
        }

        if (changedValue === 'gross' || changedValue === 'net') {
            this.calculationType.setValue(changedValue);
        }
    };

    public getFormValue = (): putRollingLimits.LimitObjectType | null => {
        this.form.setAsVisited();
        const { result } = this.form;

        if (result.value.type === 'ok') {
            if (
                this.limitType.value === 'rolling' ||
                this.limitType.value === 'calendar' ||
                this.limitType.value === 'lifetime'
            ) {
                const parsedLimitValueMax =
                    result.value.data.limitValueMax === 0 ? null : result.value.data.limitValueMax;

                if (parsedLimitValueMax !== null && result.value.data.pendingLimitValue > parsedLimitValueMax) {
                    return null;
                }

                const calculationType: DepositLimitCalculationType =
                    this.configState.config.depositLimitCalculationTypeConfigurable === false ||
                    this.isGrossCalculationAllowed === false
                        ? 'net'
                        : this.calculationType.value === 'gross' || this.calculationType.value === 'net'
                          ? this.calculationType.value
                          : 'net';

                return {
                    ...result.value.data,
                    limitValueMax: parsedLimitValueMax,
                    limitType: this.limitType.value,
                    calculationType,
                    triggersWarningEmail: this.effectiveTriggersWarningEmail,
                };
            }
        }

        return null;
    };

    @action public loadFromLimitValue = (limit: putRollingLimits.LimitObjectType): void => {
        this.selectedPeriod.setValue(String(limit.windowLengthInDays));
        this.limitType.setValue(limit.limitType);
        this.calculationType.setValue(limit.calculationType);
        this.syncCalculationTypeWithLimitType();
        this.limitValue.setValue(this.formatAmountFromCents(limit.pendingLimitValue));
        this.limitValueMax.setValue(
            limit.limitValueMax === null ? '' : this.formatAmountFromCents(limit.limitValueMax)
        );
        this.triggersWarningEmail.setValue(limit.triggersWarningEmail);
        this.isHardLimit.setValue(limit.hardLimit);
        this.syncSendEmailWarningVisibility();
    };

    private formatAmountFromCents = (amountInCents: number): string => {
        return new Amount(amountInCents.toString()).div(100).value;
    };
}

export { CALCULATION_TYPE_ARRAY, LIMIT_TYPE_ARRAY };
